import { authenticatedFetch } from '../authenticatedFetch';
import type { EpisodeQueryFilters } from '../types';
import { values } from '../utils/filterValues';

export interface ResolvedReviewedAgainstFilters {
  filters: EpisodeQueryFilters;
  empty: boolean;
}

/**
 * Resolve `reviewed_against_sop_ids` to a concrete episode id filter.
 *
 * This keeps the "Reviewed against" chip semantics identical across episode
 * list, dataset episode list, and stats hooks: fetch each selected Review SOP's
 * approved episode ids in parallel, intersect those sets, compose with any
 * explicit `id` filter, and then drop `reviewed_against_sop_ids` from the
 * returned filters so downstream paths can't double-apply it later.
 */
export async function resolveReviewedAgainstFilters(
  filters: EpisodeQueryFilters,
  signal?: AbortSignal,
): Promise<ResolvedReviewedAgainstFilters> {
  const reviewedAgainstSops = values(filters.reviewed_against_sop_ids).filter(
    (id): id is string => typeof id === 'string' && id.length > 0,
  );
  const { reviewed_against_sop_ids: _reviewedAgainstSopIds, ...baseFilters } = filters;
  void _reviewedAgainstSopIds;

  if (reviewedAgainstSops.length === 0) {
    return { filters: baseFilters, empty: false };
  }

  const perSopIds = await Promise.all(
    reviewedAgainstSops.map(async (sopId) => {
      const { episode_ids } = await authenticatedFetch<{ episode_ids: string[] }>(
        `/episodes/reviewed-against?review_sop_id=${encodeURIComponent(sopId)}`,
        { signal },
      );
      return new Set(episode_ids);
    }),
  );
  const intersection =
    perSopIds.length === 1
      ? Array.from(perSopIds[0])
      : Array.from(perSopIds[0]).filter((id) =>
          perSopIds.slice(1).every((set) => set.has(id)),
        );

  const incomingIds = values(filters.id).filter(
    (id): id is string => typeof id === 'string' && id.length > 0,
  );
  const incomingIdSet = new Set(incomingIds);
  const composedIds = incomingIds.length
    ? intersection.filter((id) => incomingIdSet.has(id))
    : intersection;

  if (composedIds.length === 0) {
    return { filters: baseFilters, empty: true };
  }

  return { filters: { ...baseFilters, id: composedIds }, empty: false };
}
